// uiLock.js
// Robust recovery from "stuck dark overlay" lock-ups.
//
// MUI renders Dialog/Modal/Menu content into a portal that contains a
// `.MuiBackdrop-root` (the dark, fixed, full-screen layer) plus the dialog/menu
// "paper". When the owning component unmounts mid-transition — e.g. navigating to
// /login on logout or a 401 while a dialog/menu is open — MUI can leave the portal
// (or just the backdrop) behind, together with body scroll-lock styles
// (`overflow: hidden`, `padding-right`, `pointer-events`) and an `aria-hidden` on
// #root. The result: a dark overlay covering everything with nothing clickable.
//
// `releaseStuckOverlays` removes ONLY orphaned overlays — a dark backdrop with no
// visible paper on top of it — so it never closes a dialog/menu that is genuinely
// open. With `{ force: true }` (used where no modal should exist, e.g. the login
// page) it clears every overlay portal.

function hasVisiblePaper(portal) {
  try {
    const paper = portal.querySelector(
      '.MuiPaper-root, [role="dialog"], [role="menu"], [role="listbox"], [role="tooltip"]'
    );
    return !!(paper && typeof paper.getClientRects === 'function' && paper.getClientRects().length > 0);
  } catch (_) {
    return false;
  }
}

function restoreBodyInteractivity() {
  try {
    const b = document.body;
    if (b && b.style) {
      b.style.overflow = '';
      b.style.paddingRight = '';
      b.style.pointerEvents = '';
    }
    // MUI sets aria-hidden="true" on the app root while a modal is open.
    const root = document.getElementById('root');
    if (root && root.getAttribute('aria-hidden') === 'true') {
      root.removeAttribute('aria-hidden');
    }
  } catch (_) {}
}

/**
 * Remove orphaned MUI overlay portals / backdrops that are blocking the UI.
 * @param {{ force?: boolean }} [opts] force=true removes every overlay portal
 *        (use only where no modal should be open, e.g. the login screen).
 * @returns {boolean} whether anything was removed.
 */
export function releaseStuckOverlays(opts = {}) {
  const force = opts.force === true;
  if (typeof document === 'undefined') return false;
  let removed = false;
  try {
    const portals = document.querySelectorAll('.MuiModal-root, .MuiDialog-root');
    portals.forEach((portal) => {
      // A *visible*, non-invisible backdrop is the dark layer that blocks clicks.
      const darkBackdrop = portal.querySelector('.MuiBackdrop-root:not(.MuiBackdrop-invisible)');
      const orphaned = !!darkBackdrop && !hasVisiblePaper(portal);
      if (force || orphaned) {
        portal.remove();
        removed = true;
      }
    });

    // Any dark backdrop left outside a modal portal is always orphaned.
    document.querySelectorAll('.MuiBackdrop-root:not(.MuiBackdrop-invisible)').forEach((bd) => {
      if (!bd.closest('.MuiModal-root, .MuiDialog-root')) {
        bd.remove();
        removed = true;
      }
    });

    if (force || removed) restoreBodyInteractivity();
  } catch (_) {}
  return removed;
}

/**
 * Start a lightweight watchdog that periodically clears orphaned overlays.
 * Returns a cleanup function. Safe to run while the app is in use: it only acts
 * on a dark backdrop that has no visible dialog/menu on top of it.
 */
export function startOverlayWatchdog(intervalMs = 1000) {
  if (typeof window === 'undefined') return () => {};
  const id = setInterval(() => releaseStuckOverlays(), intervalMs);
  return () => clearInterval(id);
}
